import { type LanguageCode, SUPPORTED_LANGUAGES, toLanguageCode } from '@ophelos/shared/utils/languages';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import csTranslations from '../locales/cs.json';
import daTranslations from '../locales/da.json';
import deTranslations from '../locales/de.json';
import elTranslations from '../locales/el.json';
import enTranslations from '../locales/en.json';
import esTranslations from '../locales/es.json';
import fiTranslations from '../locales/fi.json';
import frTranslations from '../locales/fr.json';
import huTranslations from '../locales/hu.json';
import itTranslations from '../locales/it.json';
import nbTranslations from '../locales/nb.json';
import nlTranslations from '../locales/nl.json';
import ptTranslations from '../locales/pt.json';
import skTranslations from '../locales/sk.json';
import svTranslations from '../locales/sv.json';

/**
 * Statically imported so the bundler can see every locale. Keyed exhaustively
 * by the registry's code union, so a language added to `SUPPORTED_LANGUAGES`
 * without an import here is a type error rather than a silent English fallback.
 */
const TRANSLATIONS: Record<LanguageCode, Record<string, unknown>> = {
  en: enTranslations,
  pt: ptTranslations,
  fr: frTranslations,
  cs: csTranslations,
  da: daTranslations,
  nl: nlTranslations,
  fi: fiTranslations,
  de: deTranslations,
  el: elTranslations,
  hu: huTranslations,
  it: itTranslations,
  nb: nbTranslations,
  sk: skTranslations,
  es: esTranslations,
  sv: svTranslations,
};

const resources = Object.fromEntries(SUPPORTED_LANGUAGES.map(({ code }) => [code, { translation: TRANSLATIONS[code] }]));

// Get initial language from localStorage to match LanguageContext
const LANGUAGE_STORAGE_KEY = 'customer-portal-language';
function getInitialLanguage(): string {
  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored) return toLanguageCode(stored);
    } catch {
      // localStorage can throw in private browsing mode
    }
  }
  return 'en';
}

// Initialize i18n
i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialLanguage(), // Initialize with language from localStorage to match LanguageContext
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    react: {
      useSuspense: false, // Disable suspense to avoid loading issues
    },
  });

/**
 * Change the i18n language based on LanguageContext language code
 * @param languageCode - Language code from LanguageContext (e.g., 'en-GB', 'pt-PT')
 */
export function changeLanguage(languageCode: string): void {
  i18n.changeLanguage(toLanguageCode(languageCode));
}

export default i18n;
